import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Ban, Eye, Check, X, Shield, ShieldCheck, Database, RefreshCw, Activity, Mail, Phone, Star, Car, Calendar, Copy, Building2 } from 'lucide-react';

export const AdminUsersTab = ({
  users, searchTerm, setSearchTerm, editingUser, setEditingUser,
  handleUserUpdate, handleBanUser, handleVerifyToggle, updatingStats, updateAllDriverStats,
  loadDashboardData, testEndpoints
}) => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 1500);
  };

  const filteredUsers = users.filter(user => {
    if (!user) return false;
    const search = searchTerm.toLowerCase();
    return (
      (user.firstName?.toLowerCase().includes(search) || '') ||
      (user.lastName?.toLowerCase().includes(search) || '') ||
      (user.email?.toLowerCase().includes(search) || '')
    );
  });

  return (
    <div className="users-content" data-testid="admin-users-tab">
      <div className="section-header">
        <h3>{t('admin.users.management')}</h3>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={20} />
            <input type="text" placeholder={t('admin.users.search')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <button className="btn-small" onClick={updateAllDriverStats} disabled={updatingStats}>
            <Database size={16} />
            {updatingStats ? t('admin.actions.updating') : t('admin.users.statistics')}
          </button>
        </div>
      </div>
      
      {users.length === 0 ? (
        <div className="empty-state">
          <p>{t('admin.users.noUsersFound')}</p>
          <button onClick={loadDashboardData} className="btn-primary"><RefreshCw size={16} /> {t('admin.actions.reload')}</button>
          <button onClick={testEndpoints} className="btn-secondary"><Activity size={16} /> {t('admin.actions.apiTest')}</button>
        </div>
      ) : (
        <div className="users-table" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: '700px' }}>
            <thead>
              <tr>
                <th>{t('admin.users.name')}</th>
                <th>{t('admin.users.email')}</th>
                <th>{t('b2b.orgCompany', 'Org / Firma')}</th>
                <th>{t('admin.users.role')}</th>
                <th>{t('admin.users.status')}</th>
                <th style={{ textAlign: 'center' }}>{t('admin.users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td style={{ fontWeight: 600 }}>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}</td>
                  <td style={{ fontSize: '0.85rem', color: '#64748b' }}>{user.email || '-'}</td>
                  <td>
                    {user.org_name ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', background: 'rgba(99,102,241,0.15)', color: '#a5b4fc' }}>
                        <Building2 size={12} /> {user.org_name}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#64748b' }}>{t('b2b.private', 'Privat')}</span>
                    )}
                  </td>
                  <td><span className={`role-badge ${user.role?.toLowerCase()}`}>{user.role || t('common.passenger')}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      {user.suspended ? 
                        <span className="status-badge banned">{t('admin.users.blocked')}</span> : 
                        <span className="status-badge active">{t('admin.users.active')}</span>
                      }
                      {user.verified && <Shield size={12} title={t('admin.users.verified')} style={{ color: '#2563eb' }} />}
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons" style={{ justifyContent: 'center' }}>
                      <button
                        data-testid={`view-user-${user.id}`}
                        onClick={() => setSelectedUser(user)}
                        className="action-button edit"
                        title={t('admin.users.viewDetails')}
                        style={{ background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.35rem 0.6rem' }}
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className={`action-button ${user.suspended ? 'unban' : 'ban'}`} 
                        onClick={() => handleBanUser(user.id, user.suspended)}
                        title={user.suspended ? t('admin.users.unban') : t('admin.users.ban')}
                      >
                        <Ban size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* USER DETAIL MODAL */}
      {selectedUser && (
        <div
          data-testid="user-detail-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '1rem'
          }}
          onClick={() => { setSelectedUser(null); setEditingUser(null); }}
        >
          <div
            style={{
              background: 'white', borderRadius: '16px', maxWidth: '560px', width: '100%',
              maxHeight: '85vh', overflowY: 'auto', padding: '0', boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
              wordWrap: 'break-word', overflowWrap: 'break-word'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                {editingUser?.id === selectedUser.id ? t('common.edit') : t('admin.users.viewDetails')}
              </h3>
              <button
                data-testid="close-user-modal"
                onClick={() => { setSelectedUser(null); setEditingUser(null); }}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '0.4rem', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem 2rem' }}>
              {/* Avatar + Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1.2rem'
                }}>
                  {(selectedUser.firstName?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  {editingUser?.id === selectedUser.id ? (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        data-testid="edit-user-firstname"
                        value={editingUser.firstName || ''}
                        onChange={e => setEditingUser({ ...editingUser, firstName: e.target.value })}
                        placeholder={t('admin.users.name')}
                        style={{ border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0.4rem 0.7rem', fontSize: '0.95rem', width: '120px' }}
                      />
                      <input
                        data-testid="edit-user-lastname"
                        value={editingUser.lastName || ''}
                        onChange={e => setEditingUser({ ...editingUser, lastName: e.target.value })}
                        placeholder={t('admin.users.name')}
                        style={{ border: '2px solid #e2e8f0', borderRadius: '8px', padding: '0.4rem 0.7rem', fontSize: '0.95rem', width: '120px' }}
                      />
                    </div>
                  ) : (
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: '1.05rem' }}>
                      {`${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || '-'}
                    </h4>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
                    <span className={`role-badge ${selectedUser.role?.toLowerCase()}`} style={{ fontSize: '0.75rem' }}>
                      {selectedUser.role || t('common.passenger')}
                    </span>
                    {selectedUser.verified && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#2563eb', fontSize: '0.75rem', fontWeight: 600 }}>
                        <Shield size={12} /> {t('admin.users.verified')}
                      </span>
                    )}
                    {selectedUser.suspended && (
                      <span className="status-badge banned" style={{ fontSize: '0.7rem' }}>{t('admin.users.blocked')}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '10px' }}>
                  <Mail size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>{t('admin.users.email')}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedUser.email || '-'}</div>
                  </div>
                  {selectedUser.email && (
                    <button
                      data-testid="copy-email-btn"
                      onClick={() => copyEmail(selectedUser.email)}
                      title={copiedEmail ? 'Copied!' : 'Copy Email'}
                      style={{
                        background: copiedEmail ? '#dcfce7' : '#f1f5f9', border: '1px solid ' + (copiedEmail ? '#86efac' : '#e2e8f0'),
                        borderRadius: '6px', padding: '0.3rem', cursor: 'pointer', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: copiedEmail ? '#16a34a' : '#64748b', transition: 'all 0.2s'
                      }}
                    >
                      {copiedEmail ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '10px' }}>
                  <Phone size={16} style={{ color: '#64748b' }} />
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>{t('admin.users.phone')}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedUser.phone || '-'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '10px' }}>
                  <Star size={16} style={{ color: '#eab308' }} />
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>{t('admin.users.rating')}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      {selectedUser.average_rating ? `${Number(selectedUser.average_rating).toFixed(1)} (${selectedUser.total_ratings || 0})` : '-'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '10px' }}>
                  <Car size={16} style={{ color: '#64748b' }} />
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>{t('admin.users.rides')}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{selectedUser.total_rides || 0}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#f8fafc', borderRadius: '10px' }}>
                  <Calendar size={16} style={{ color: '#64748b', flexShrink: 0 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>ID</div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all', overflowWrap: 'break-word' }}>{selectedUser.id}</div>
                  </div>
                </div>
                {selectedUser.is_admin && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '10px' }}>
                    <Shield size={16} style={{ color: '#d97706' }} />
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#92400e' }}>Admin</div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', background: selectedUser.org_name ? '#e0e7ff' : '#f8fafc', borderRadius: '10px' }}>
                  <Building2 size={16} style={{ color: selectedUser.org_name ? '#6366f1' : '#64748b' }} />
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>{t('b2b.orgCompany', 'Org / Firma')}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, color: selectedUser.org_name ? '#4f46e5' : '#94a3b8' }}>
                      {selectedUser.org_name || t('b2b.private', 'Privat')}
                      {selectedUser.b2b_role && <span style={{ fontSize: '11px', marginLeft: '6px', color: '#94a3b8' }}>({t(`b2b.roles.${selectedUser.b2b_role}`, selectedUser.b2b_role)})</span>}
                    </div>
                  </div>
                </div>
                {/* Verification Status */}
                <div data-testid="verification-status-row" style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem', borderRadius: '10px',
                  background: selectedUser.identity_verified ? '#dcfce7' : '#fee2e2',
                }}>
                  <ShieldCheck size={16} style={{ color: selectedUser.identity_verified ? '#16a34a' : '#ef4444' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600 }}>
                      {t('verification.badge')}
                    </div>
                    <div style={{
                      fontSize: '0.85rem', fontWeight: 600,
                      color: selectedUser.identity_verified ? '#15803d' : '#dc2626',
                    }}>
                      {selectedUser.identity_verified
                        ? `${t('verification.badge')} (${selectedUser.verification_provider || 'Admin'})`
                        : 'Not Verified'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '1rem 2rem', borderTop: '1px solid #e2e8f0',
              display: 'flex', justifyContent: 'space-between', gap: '0.75rem'
            }}>
              {editingUser?.id === selectedUser.id ? (
                <>
                  <button
                    data-testid="save-user-btn"
                    onClick={() => { handleUserUpdate(); setSelectedUser(null); }}
                    style={{
                      flex: 1, padding: '0.65rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: '#10b981', color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                    }}
                  >
                    <Check size={16} /> {t('common.save')}
                  </button>
                  <button
                    onClick={() => setEditingUser(null)}
                    style={{
                      flex: 1, padding: '0.65rem', borderRadius: '10px', border: '1px solid #e2e8f0', cursor: 'pointer',
                      background: 'white', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                    }}
                  >
                    <X size={16} /> {t('common.cancel')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="edit-user-btn"
                    onClick={() => setEditingUser({ ...selectedUser })}
                    style={{
                      flex: 1, padding: '0.65rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', fontWeight: 600
                    }}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    data-testid="verify-user-btn"
                    onClick={() => { handleVerifyToggle(selectedUser.id, !selectedUser.identity_verified); setSelectedUser(null); }}
                    style={{
                      flex: 1, padding: '0.65rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: selectedUser.identity_verified ? '#f59e0b' : '#10b981', color: 'white', fontWeight: 600,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem'
                    }}
                  >
                    <ShieldCheck size={16} />
                    {selectedUser.identity_verified ? t('admin.users.unverify') || 'Unverify' : t('admin.users.verify') || 'Verify'}
                  </button>
                  <button
                    data-testid="ban-user-btn"
                    onClick={() => { handleBanUser(selectedUser.id, selectedUser.suspended); setSelectedUser(null); }}
                    style={{
                      flex: 1, padding: '0.65rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: selectedUser.suspended ? '#10b981' : '#ef4444', color: 'white', fontWeight: 600
                    }}
                  >
                    {selectedUser.suspended ? t('admin.users.unban') : t('admin.users.ban')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
